/**
 * ExpandingCard - Main container for the aircraft card UI
 *
 * Layout: Compact card (fixed size) + Detached Detail Panel (absolute positioned)
 *
 * The compact card stays centered on the aircraft. The detail panel appears as
 * a separate floating island to the right (or left if near viewport edge).
 *
 * Renders based on cardState from aircraftCardStore:
 * - closed    → null
 * - compact   → VerticalTabBar + CompactCardHeader (320px fixed)
 * - expanded  → Same + DetailPanel as separate island
 * - moveBar   → null (MoveBar rendered separately in RampPage)
 * - fullModal → null (FullModal rendered separately in RampPage)
 */

import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { CompactCardHeader } from './CompactCardHeader'
import { VerticalTabBar } from './VerticalTabBar'
import { DetailPanel } from './DetailPanel'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface ExpandingCardProps {
  data: AllocationEnrichedData
  /** Screen X position of the card anchor point */
  screenX?: number
}

/**
 * Get the color-coded top border class based on contract type
 */
function getTopBorderClass(data: AllocationEnrichedData): string {
  if (data.leaseContract) return 'border-t-2 border-t-blue-500'
  if (data.transientStay) return 'border-t-2 border-t-amber-500'
  return 'border-t-2 border-t-muted-foreground/30'
}

/** Compact card width: VerticalTabBar (40px) + CompactCardHeader (280px) */
const COMPACT_CARD_WIDTH = 320
/** Detail panel width */
const DETAIL_PANEL_WIDTH = 260
/** Gap between compact card and detail panel */
const PANEL_GAP = 8

export function ExpandingCard({ data, screenX }: ExpandingCardProps) {
  const mode = useAircraftCardStore((s) => s.cardState.mode)

  // Only render in compact or expanded modes
  if (mode !== 'compact' && mode !== 'expanded') {
    return null
  }

  const topBorder = getTopBorderClass(data)
  const isExpanded = mode === 'expanded'

  // Determine if detail panel should be on left or right
  // Default to right, flip to left if would overflow viewport
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
  const cardCenterX = screenX ?? viewportWidth / 2
  const cardRightEdge = cardCenterX + COMPACT_CARD_WIDTH / 2
  const panelWouldOverflow = cardRightEdge + PANEL_GAP + DETAIL_PANEL_WIDTH > viewportWidth - 20
  const panelOnLeft = panelWouldOverflow

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {/* Compact card - fixed size, always centered on aircraft */}
      <div
        className={`flex flex-row items-stretch bg-background/95 backdrop-blur-md border rounded-lg shadow-lg overflow-hidden ${topBorder}`}
        style={{ width: COMPACT_CARD_WIDTH }}
      >
        {/* Vertical tab icons */}
        <VerticalTabBar />

        {/* Compact card body */}
        <CompactCardHeader data={data} />
      </div>

      {/* Detail panel - absolute positioned as separate island */}
      {isExpanded && (
        <div
          className={`
            absolute top-0
            ${panelOnLeft ? 'right-full mr-2' : 'left-full ml-2'}
            animate-in duration-200
            ${panelOnLeft ? 'slide-in-from-right-2' : 'slide-in-from-left-2'}
          `}
        >
          <div
            className={`bg-background/95 backdrop-blur-md border rounded-lg shadow-lg ${topBorder}`}
            style={{ width: DETAIL_PANEL_WIDTH }}
          >
            <DetailPanel data={data} />
          </div>
        </div>
      )}
    </div>
  )
}
